// src/services/onboardingService.js
// =====================================================
// FuMind_GM HR Module - Phase 3D Onboarding Service
// Fixed: Update Task 400 diagnostics + no null dueDate
// =====================================================

const HR_ONBOARDING_GET_ALL_FLOW_URL = "/tracka-flow/v1/HR_ONBOARDING_GET_ALL";
const HR_ONBOARDING_GET_BY_EMPLOYEE_FLOW_URL = "/tracka-flow/v1/HR_ONBOARDING_GET_BY_EMPLOYEE";
const HR_ONBOARDING_INITIALIZE_FLOW_URL = "/tracka-flow/v1/HR_ONBOARDING_INITIALIZE";
const HR_ONBOARDING_UPDATE_TASK_FLOW_URL = "/tracka-flow/v1/HR_ONBOARDING_UPDATE_TASK";
const HR_ONBOARDING_ACTIVATE_EMPLOYEE_FLOW_URL = "/tracka-flow/v1/HR_ONBOARDING_ACTIVATE_EMPLOYEE";

const API_KEY = "";

async function parseJsonResponse(response) {
  const responseText = await response.text();

  if (!responseText) return {};

  try {
    return JSON.parse(responseText);
  } catch {
    return { message: responseText, rawText: responseText };
  }
}

function normalizePowerAutomateResponse(rawData) {
  return rawData?.body || rawData || {};
}

function throwIfFlowFailed(response, data, fallbackMessage) {
  if (!response.ok || data?.success === false) {
    const flowMessage = data?.message || data?.error?.message || data?.rawText || "";
    const statusText = `${response.status} ${response.statusText}`.trim();
    throw new Error(flowMessage ? `${fallbackMessage}: ${flowMessage}` : `${fallbackMessage}. HTTP ${statusText}`);
  }
}

function assertUrlConfigured(url, placeholder, flowName) {
  if (!url || url === placeholder) {
    throw new Error(`${flowName} flow URL is not configured in onboardingService.js`);
  }
}

export async function getAllOnboardingTasks(filters = {}) {
  assertUrlConfigured(HR_ONBOARDING_GET_ALL_FLOW_URL, "PASTE_HR_ONBOARDING_GET_ALL_FLOW_URL_HERE", "HR_Onboarding_Get_All");

  const response = await fetch(HR_ONBOARDING_GET_ALL_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({

      employeeId: filters.employeeId || "",
      workEmail: filters.workEmail || "",
      taskStatus: filters.taskStatus || "",
      taskOwner: filters.taskOwner || ""
    })
  });

  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);
  throwIfFlowFailed(response, data, "Failed to load onboarding tasks");
  return Array.isArray(data.tasks) ? data.tasks : [];
}

export async function getOnboardingTasksByEmployee({ employeeId, workEmail } = {}) {
  assertUrlConfigured(HR_ONBOARDING_GET_BY_EMPLOYEE_FLOW_URL, "PASTE_HR_ONBOARDING_GET_BY_EMPLOYEE_FLOW_URL_HERE", "HR_Onboarding_Get_By_Employee");

  const response = await fetch(HR_ONBOARDING_GET_BY_EMPLOYEE_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({

      employeeId: employeeId || "",
      workEmail: workEmail || ""
    })
  });

  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);
  throwIfFlowFailed(response, data, "Failed to load employee onboarding checklist");
  return Array.isArray(data.tasks) ? data.tasks : [];
}

export async function initializeOnboardingChecklist({ employeeId, workEmail, employeeName = "", initializedBy = "" } = {}) {
  assertUrlConfigured(HR_ONBOARDING_INITIALIZE_FLOW_URL, "PASTE_HR_ONBOARDING_INITIALIZE_FLOW_URL_HERE", "HR_Onboarding_Initialize_Checklist");

  if (!employeeId) throw new Error("Employee ID is required to initialize onboarding checklist.");

  const response = await fetch(HR_ONBOARDING_INITIALIZE_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({

      employeeId,
      workEmail: workEmail || "",
      employeeName: employeeName || "",
      initializedBy: initializedBy || ""
    })
  });

  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);
  throwIfFlowFailed(response, data, "Failed to initialize onboarding checklist");
  return data;
}

export async function updateOnboardingTask({
  onboardingId,
  taskName,
  taskStatus,
  taskOwner,
  dueDate,
  comments,
  completedBy
} = {}) {
  assertUrlConfigured(HR_ONBOARDING_UPDATE_TASK_FLOW_URL, "PASTE_HR_ONBOARDING_UPDATE_TASK_FLOW_URL_HERE", "HR_Onboarding_Update_Task");

  if (!onboardingId) throw new Error("Onboarding ID is required to update task.");

  const payload = {

    onboardingId,
    taskName: taskName || "",
    taskStatus: taskStatus || "Pending",
    taskOwner: taskOwner || "HR",
    // Important: send empty string, not null. Null can be rejected by HTTP trigger schema before run history is created.
    dueDate: dueDate || "",
    comments: comments || "",
    completedBy: completedBy || ""
  };

  console.log("HR_Onboarding_Update_Task payload:", payload);

  const response = await fetch(HR_ONBOARDING_UPDATE_TASK_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });

  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);
  console.log("HR_Onboarding_Update_Task response:", response.status, data);
  throwIfFlowFailed(response, data, "Failed to update onboarding task");
  return data;
}

export async function activateEmployeeFromOnboarding({ employeeId, workEmail, activatedBy = "", comments = "" } = {}) {
  assertUrlConfigured(HR_ONBOARDING_ACTIVATE_EMPLOYEE_FLOW_URL, "PASTE_HR_ONBOARDING_ACTIVATE_EMPLOYEE_FLOW_URL_HERE", "HR_Onboarding_Activate_Employee");

  if (!employeeId) throw new Error("Employee ID is required to activate employee.");

  const response = await fetch(HR_ONBOARDING_ACTIVATE_EMPLOYEE_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({

      employeeId,
      workEmail: workEmail || "",
      activatedBy: activatedBy || "",
      comments: comments || ""
    })
  });

  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);
  throwIfFlowFailed(response, data, "Failed to activate employee");
  return data;
}
